import { ReactElement, MouseEvent } from "react";
import { className } from "../../../../shared/helpers";
import { useTimeMachine, useTimeMachineUpdate } from "../../../../contexts/TimeMachineContext";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import { IAverageIndicatorStatusHistory } from "../../../../services/http/indicatorstatushistory/models/IAverageIndicatorStatusHistory";
import { GetIndicatorStatusHistoryByDayParameters } from "../../../../services/http/indicatorstatushistory/models/GetIndicatorStatusHistoryByDayParameters";
import { IndicatorStatusHistoryService } from "../../../../services/http/indicatorstatushistory/IndicatorStatusHistoryService";
import { DateService } from "../../../../services/types/DateService";
import Spinner from "../../Spinner";
import { useTooltip } from "../../../../hooks/UseTooltip";
import styles from './StatusHistory.module.scss';

interface IStatusHistoryProps
{
    componentId: number,
    indicatorId: number,
    hasFullWidth: boolean
}

function getClassName(status: number): string | null
{
    switch (status)
    {
        case 0: return "background-color-offline";
        case 1: return "background-color-safe";
        case 2: return "background-color-warning";
        case 3: return "background-color-danger";
        case 4: return "background-color-failure";
    }

    return null;
}

async function loadData(service: IndicatorStatusHistoryService, componentId: number, indicatorId: number, date: Date): Promise<IAverageIndicatorStatusHistory[]>
{
    var parameters = new GetIndicatorStatusHistoryByDayParameters();
    parameters.componentId = componentId;
    parameters.indicatorId = indicatorId;
    parameters.daysBehind = 30;
    parameters.date = date ?? new Date();
    if (date)
    {
        return await service.getIndicatorStatusHistoryByDay(parameters);
    }else
    {
        return await service.getIndicatorStatusHistoryByDayRealtTime(componentId, indicatorId);
    }
    
}

function isFullView(): boolean
{
    return window.innerWidth >= 992;
}

const StatusHistory = (props: IStatusHistoryProps): ReactElement =>
{
    const service = useDependency(IndicatorStatusHistoryService);
    const dateService = useDependency(DateService);
    const timeMachineUpdate = useTimeMachineUpdate();
    const { setupTooltip, clearTooltip, Tooltip } = useTooltip();
    const timeMachineDate = useTimeMachine();
    const { loading, content: history } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"),loadData, service, props.componentId, props.indicatorId, timeMachineDate);
    //To avoid forcing a re-render and re-request the information to the server, just hide the extra days in the client
    const historyOnScreen = !history
        ? []
        : history && props.hasFullWidth && isFullView()
            ? history
            : history.slice(history.length - 10, history.length);

    function handleMouseEnter(e: MouseEvent, indicator: IAverageIndicatorStatusHistory): void
    {
        const { x, y, height } = e.currentTarget.getBoundingClientRect();
        const computedStyles = window.getComputedStyle(document.documentElement);
        const tooltipWidth = parseFloat(computedStyles.getPropertyValue("--tooltip-width"));
        const newX = x - tooltipWidth;
        const newY = y - height - 10;

        setupTooltip({
            position: { x: newX, y: newY },
            customClassName: styles.tooltip,
            content: getTooltipText(indicator)
        })
    }

    function getTooltipText(indicator: IAverageIndicatorStatusHistory | null): ReactElement | null
    {
        if (!indicator) return null;

        const statusText = indicator.indicatorStatusId === 0 ? 'No measures received.' : `${(indicator.score * 100).toFixed(2)}%`;
        return <span className={styles.toolTipText}>{`Date: ${new Date(indicator.date).toDateString()} | Status: ${statusText}`}</span>;
    }

    return loading
        ? <Spinner caption="Loading widget..." />
        : <>
            <div {...className(styles.statusGrid)} onMouseLeave={clearTooltip}>
                {historyOnScreen && historyOnScreen.map((x, i) =>
                    <div
                        key={i}
                        data-tip={getTooltipText(x)}
                        data-for="statusHistoryTooltip"
                        onClick={() => timeMachineUpdate(new Date(x.date))}
                        onMouseEnter={e => handleMouseEnter(e, x)}
                        {...className(
                            styles.indicatorSemaphore,
                            getClassName(x.indicatorStatusId),
                            { [styles.fullWidthIndicator]: i > 9 }
                        )}
                    >
                        <div
                            aria-hidden="true"
                            {...className(styles.indicatorInner, getClassName(x.indicatorStatusId))}
                        />
                    </div>
                )}
            </div>

            {history && history.length > 0 && historyOnScreen && historyOnScreen.length > 0 && (
                <div className={styles.statusDates}>
                    <span className={styles.smallWidthIndicator}>{dateService.format(new Date(historyOnScreen[0].date), 'MM/dd/yy')}</span>
                    <span className={styles.fullWidthIndicator}>{dateService.format(new Date(history[0].date), 'MM/dd/yy')}</span>
                    <span>{dateService.format(new Date(historyOnScreen[historyOnScreen.length - 1].date), 'MM/dd/yy')}</span>
                </div>
            )}

            <Tooltip />
        </>;
}

export default StatusHistory;